import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { formatDateTime, formatNumber } from "@/lib/format";
import { useAuth } from "@/lib/AuthContext";
import { PageHeader } from "@/components/portal/PortalLayout";
import {
  Badge,
  Button,
  Card,
  Empty,
  ErrorNote,
  Field,
  Loading,
  Modal,
  Note,
  inputClass,
} from "@/components/portal/Ui";

const STATUSES = ["New", "Reviewing", "Approved", "Declined"];
const TONE = {
  New: "orange",
  Reviewing: "neutral",
  Approved: "green",
  Declined: "red",
};

function Socials({ socials }) {
  return (
    <div className="space-y-0.5">
      {socials.map((s, i) => (
        <p key={i} className="text-sm">
          <span className="font-semibold">{s.handle}</span>
          <span className="body-text"> · {s.platform}</span>
          {s.followers != null && (
            <span className="body-text"> · {formatNumber(s.followers)} followers</span>
          )}
        </p>
      ))}
    </div>
  );
}

export default function CreatorApplications() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [detail, setDetail] = useState(null);
  const [action, setAction] = useState(null);
  const [inviteLink, setInviteLink] = useState(null);

  const status = params.get("status") || "";

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/creator-applications", {
        params: status ? { status } : {},
      });
      setRows(data);
      setError("");
    } catch (e) {
      setError(apiError(e));
      setRows([]);
    } finally {
      setRefreshing(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = (rows || []).reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  if (!rows) return error ? <ErrorNote message={error} onRetry={load} /> : <Loading />;

  return (
    <div data-testid="creator-applications-page">
      <PageHeader
        title="Creator Applications"
        description="Creators who applied through the public website. Approving one creates their account and sends an activation link."
        onRefresh={load}
        refreshing={refreshing}
      />
      <ErrorNote message={error} onRetry={load} />

      <Card className="mb-4" testId="applications-filters">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-[0.12em] text-[#8a8378]">
            Status
          </span>
          <button
            type="button"
            onClick={() => setParams({})}
            data-testid="filter-status-all"
            className={`rounded-full border px-3.5 py-1.5 text-sm font-bold transition-colors ${
              status === "" ? "border-[#111111] bg-[#111111] text-white" : "border-[#E6E1D8]"
            }`}
          >
            All
          </button>
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setParams({ status: s })}
              data-testid={`filter-status-${s.toLowerCase()}`}
              className={`rounded-full border px-3.5 py-1.5 text-sm font-bold transition-colors ${
                status === s ? "border-[#111111] bg-[#111111] text-white" : "border-[#E6E1D8]"
              }`}
            >
              {s}
              {counts[s] ? ` (${counts[s]})` : ""}
            </button>
          ))}
        </div>
      </Card>

      {rows.length === 0 ? (
        <Empty
          title="No applications yet"
          body="Applications submitted from the public 'Join the creator pool' page appear here."
          testId="applications-empty"
        />
      ) : (
        <Card className="overflow-hidden p-0" testId="applications-table-card">
          <div className="scroll-x overflow-x-auto">
            <table className="w-full min-w-[980px] text-left text-sm" data-testid="applications-table">
              <thead className="bg-[#F7F4EF]">
                <tr className="text-xs font-bold uppercase tracking-[0.1em] text-[#8a8378]">
                  <th className="px-4 py-3">Creator</th>
                  <th className="px-4 py-3">Platforms</th>
                  <th className="px-4 py-3">Categories</th>
                  <th className="px-4 py-3">Submitted</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-t border-[#EFEBE3] align-top"
                    data-testid={`application-row-${row.id}`}
                  >
                    <td className="px-4 py-3.5">
                      <p className="font-semibold">{row.display_name}</p>
                      <p className="text-xs body-text">{row.email}</p>
                      {row.city && <p className="text-xs body-text">{row.city}</p>}
                    </td>
                    <td className="px-4 py-3.5">
                      <Socials socials={row.socials} />
                    </td>
                    <td className="max-w-[16rem] px-4 py-3.5 text-sm body-text">
                      {row.categories.join(", ")}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5 text-sm body-text">
                      {formatDateTime(row.created_at)}
                    </td>
                    <td className="px-4 py-3.5">
                      <Badge tone={TONE[row.status]} testId={`application-status-${row.id}`}>
                        {row.status}
                      </Badge>
                      {row.reviewed_by_name && (
                        <p className="mt-1 text-xs body-text">by {row.reviewed_by_name}</p>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex flex-wrap justify-end gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setDetail(row)}
                          testId={`application-view-${row.id}`}
                        >
                          View
                        </Button>
                        {row.status !== "Approved" && row.status !== "Declined" && (
                          <>
                            {row.status === "New" && (
                              <Button
                                variant="outline"
                                onClick={() => setAction({ kind: "Reviewing", row })}
                                testId={`application-reviewing-${row.id}`}
                              >
                                Mark reviewing
                              </Button>
                            )}
                            {user?.role === "admin" && (
                              <Button
                                onClick={() => setAction({ kind: "approve", row })}
                                testId={`application-approve-${row.id}`}
                              >
                                Approve
                              </Button>
                            )}
                            <Button
                              variant="danger"
                              onClick={() => setAction({ kind: "Declined", row })}
                              testId={`application-decline-${row.id}`}
                            >
                              Decline
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <DetailModal application={detail} onClose={() => setDetail(null)} />
      <ActionModal
        state={action}
        onClose={() => setAction(null)}
        onDone={async (link) => {
          setAction(null);
          if (link) setInviteLink(link);
          await load();
        }}
      />

      <Modal
        open={Boolean(inviteLink)}
        onClose={() => setInviteLink(null)}
        title="Creator account created"
        testId="application-invite-modal"
      >
        {inviteLink && (
          <div className="space-y-4">
            {inviteLink.delivery?.sent ? (
              <Note tone="green">
                An activation email has been sent to <strong>{inviteLink.email}</strong>.
              </Note>
            ) : (
              <Note tone="orange" testId="application-delivery-error">
                {inviteLink.delivery?.error ||
                  "Email delivery is not configured, so no email was sent."}{" "}
                Send this activation link to the creator yourself. It expires in 7 days.
              </Note>
            )}
            {inviteLink.delivery?.link && (
              <>
                <p className="break-all rounded-xl border border-[#E6E1D8] bg-[#F7F4EF] p-3 text-xs" data-testid="application-invite-link">
                  {inviteLink.delivery.link}
                </p>
                <Button
                  variant="outline"
                  testId="application-copy-link"
                  onClick={() => {
                    navigator.clipboard?.writeText(inviteLink.delivery.link);
                    toast.success("Activation link copied");
                  }}
                >
                  <Copy className="h-4 w-4" /> Copy link
                </Button>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

function DetailModal({ application, onClose }) {
  if (!application) return null;
  const rowsOut = [
    ["Email", application.email],
    ["Phone", application.phone],
    ["City", application.city],
    ["Categories", application.categories.join(", ")],
    ["Audience", application.audience_note],
    ["Indicative rates", application.rate_note],
    ["Notes", application.note],
    ["Submitted", formatDateTime(application.created_at)],
    ["Reviewed by", application.reviewed_by_name],
    ["Reviewed at", application.reviewed_at ? formatDateTime(application.reviewed_at) : null],
    ["Review note", application.review_note],
  ];
  return (
    <Modal open onClose={onClose} title={application.display_name} wide testId="application-detail-modal">
      <div className="space-y-5">
        <Badge tone={TONE[application.status]}>{application.status}</Badge>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#8a8378]">Platforms</p>
          <div className="mt-2">
            <Socials socials={application.socials} />
          </div>
        </div>
        {application.portfolio_url && (
          <a
            href={application.portfolio_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-[#F2811D] hover:underline"
            data-testid="application-portfolio-link"
          >
            Portfolio / media kit <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
        <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
          {rowsOut
            .filter(([, value]) => value)
            .map(([label, value]) => (
              <div key={label}>
                <dt className="text-xs font-bold uppercase tracking-[0.12em] text-[#8a8378]">
                  {label}
                </dt>
                <dd className="mt-1 text-sm leading-relaxed">{value}</dd>
              </div>
            ))}
        </dl>
      </div>
    </Modal>
  );
}

function ActionModal({ state, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setNote("");
    setError("");
  }, [state]);

  if (!state) return null;
  const { kind, row } = state;
  const isApprove = kind === "approve";
  const isDecline = kind === "Declined";

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      if (isApprove) {
        const { data } = await api.post(`/creator-applications/${row.id}/approve`);
        toast.success(`${row.display_name} added as a creator`);
        await onDone({ email: row.email, delivery: data.delivery });
      } else {
        await api.patch(`/creator-applications/${row.id}`, {
          status: kind,
          review_note: note.trim(),
        });
        toast.success(isDecline ? "Application declined" : "Marked as reviewing");
        await onDone(null);
      }
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={
        isApprove
          ? `Approve ${row.display_name}`
          : isDecline
          ? `Decline ${row.display_name}`
          : "Mark as reviewing"
      }
      testId="application-action-modal"
    >
      <form onSubmit={submit} className="space-y-4" data-testid="application-action-form">
        <ErrorNote message={error} />
        {isApprove && (
          <Note tone="orange">
            This creates a creator account for <strong>{row.email}</strong> and generates a 7-day
            activation link. The creator sets their own password — you never see it.
          </Note>
        )}
        {isDecline && (
          <Field label="Reason (recorded internally)" required>
            <textarea
              required
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className={inputClass}
              data-testid="application-note-input"
            />
          </Field>
        )}
        {!isApprove && !isDecline && (
          <Field label="Internal note" hint="Optional.">
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className={inputClass}
              data-testid="application-note-input"
            />
          </Field>
        )}
        <div className="flex justify-end gap-2.5">
          <Button variant="outline" onClick={onClose} testId="application-action-cancel">
            Cancel
          </Button>
          <Button
            type="submit"
            variant={isDecline ? "danger" : "primary"}
            busy={busy}
            testId="application-action-submit"
          >
            {isApprove ? "Approve & create account" : isDecline ? "Decline" : "Save"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
